import {
  Box,
  IconButton,
  TextField,
  Typography,
  MenuItem,
  Select,
  FormHelperText,
} from "@mui/material";
import React, {
  useState,
  useEffect,
  forwardRef,
  useImperativeHandle,
} from "react";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import parsePhoneNumberFromString from "libphonenumber-js";
import getPhoneLength from "../../../utils/getPhoneNumberLength";

const API_ENDPOINT = process.env.REACT_APP_API_ENDPOINT;
const SAP_API_ENDPOINT = process.env.REACT_APP_API_SAP;

const StepperForm = forwardRef((props, ref) => {
  const [countries, setCountries] = useState([]);
  const [isdCodeList, setIsdCodeList] = useState([]);
  const [dreamDestinations, setDreamDestinations] = useState([]);
  const [broadFields, setBroadFields] = useState([]);
  const [narrowFields, setNarrowFields] = useState([]);
  const [states, setStates] = useState([]);
  const [cities, setCities] = useState([]);
  const [branches, setBranches] = useState([]);

  const [formData, setFormData] = useState({
    guardianName: "",
    guardianMobile: "",
    guardianCountryCode: "91",
    dreamDestination: "",
    broadField: "",
    narrowField: "",
    country: "",
    state: "",
    city: "",
    nearestBranch: "",
  });

  const [errors, setErrors] = useState({});

  // ==========================
  //  EXPOSE METHODS TO PARENT
  // ==========================
  useImperativeHandle(ref, () => ({
    validate: () => {
      const newErrors = {};

      // Guardian Name
      if (!formData.guardianName.trim())
        newErrors.guardianName = "Guardian name is required";
      else if (/\d/.test(formData.guardianName))
        newErrors.guardianName = "Name cannot contain numbers";

      // Mobile
      if (!formData.guardianMobile) {
        newErrors.guardianMobile = "Mobile number is required";
      } else if (!/^\d+$/.test(formData.guardianMobile)) {
        newErrors.guardianMobile = "Digits only";
      }

      // Dropdown fields
      [
        "dreamDestination",
        "broadField",
        "narrowField",
        "country",
        "state",
        "city",
        "nearestBranch",
      ].forEach((key) => {
        if (!formData[key]) newErrors[key] = "Required";
      });

      setErrors(newErrors);
      return Object.keys(newErrors).length === 0;
    },

    getData: () => ({
      guardian_name: formData.guardianName,
      guardian_mobile_country_code: formData.guardianCountryCode,
      guardian_mobile: formData.guardianMobile,
      target_country_id: formData.dreamDestination,
      country_id: formData.country,
      state_id: formData.state,
      city_id: formData.city,
      nearest_branch_id: formData.nearestBranch,
      broad_field_id: formData.broadField,
      narrow_field_id: formData.narrowField,
    }),
  }));

  // ============
  // API CALLS
  // ============
  useEffect(() => {
    fetch(`${API_ENDPOINT}countries`)
      .then((res) => res.json())
      .then((data) => {
        const sorted = [...data].sort((a, b) => a.phone_code - b.phone_code);
        const unique = sorted.filter(
          (obj, index, self) =>
            index === self.findIndex((t) => t.phone_code === obj.phone_code)
        );
        setIsdCodeList(unique);
        setCountries(data);
      });
  }, []);

  useEffect(() => {
    fetch(`${API_ENDPOINT}dream-destination`)
      .then((res) => res.json())
      .then((data) => setDreamDestinations(data?.data));
  }, []);

  useEffect(() => {
    fetch(`${SAP_API_ENDPOINT}get-broad-fields`)
      .then((res) => res.json())
      .then((data) => setBroadFields(data?.data));
  }, []);

  useEffect(() => {
    if (!formData.broadField) return;

    fetch(`${SAP_API_ENDPOINT}get-narrow-fields`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ broad_field_id: formData.broadField }),
    })
      .then((res) => res.json())
      .then((data) => setNarrowFields(data?.data));
  }, [formData.broadField]);

  useEffect(() => {
    if (!formData.country) return;

    fetch(`${API_ENDPOINT}sap-states`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ country_id: formData.country }),
    })
      .then((res) => res.json())
      .then((data) => setStates(data?.data));
  }, [formData.country]);

  useEffect(() => {
    if (!formData.state) return;

    fetch(`${API_ENDPOINT}sap-cities`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({
        country_id: formData.country,
        state_id: formData.state,
      }),
    })
      .then((res) => res.json())
      .then((data) => setCities(data?.data));
  }, [formData.state]);

  useEffect(() => {
    fetch(`${API_ENDPOINT}branches`)
      .then((res) => res.json())
      .then((data) => setBranches(data?.data));
  }, []);

  // ===============
  //   STEPS CONFIG
  // ===============
  const steps = [
    {
      key: "guardianName",
      label: "Guardian Name",
      placeholder: "Enter guardian name",
      type: "text",
    },
    {
      key: "guardianMobile",
      label: "Guardian Mobile",
      placeholder: "Enter mobile number",
      type: "mobile",
    },
    {
      key: "dreamDestination",
      label: "Dream Destination",
      placeholder: "Select destination",
      type: "dropdown",
      options: dreamDestinations,
      valueKey: "id",
      labelKey: "country_name",
    },
    {
      key: "broadField",
      label: "Area of Study - Broad Field",
      placeholder: "Select broad field",
      type: "dropdown",
      options: broadFields,
      valueKey: "id",
      labelKey: "broad_field_name",
    },
    {
      key: "narrowField",
      label: "Area of Study - Narrow Field",
      placeholder: "Select narrow field",
      type: "dropdown",
      options: narrowFields,
      valueKey: "id",
      labelKey: "narrow_field_name",
    },
    {
      key: "country",
      label: "Your Country",
      placeholder: "Select your country",
      type: "dropdown",
      options: countries,
      valueKey: "id",
      labelKey: "country_name",
    },
    {
      key: "state",
      label: "Your State",
      placeholder: "Select state",
      type: "dropdown",
      options: states,
      valueKey: "id",
      labelKey: "state_name",
    },
    {
      key: "city",
      label: "Your City",
      placeholder: "Select city",
      type: "dropdown",
      options: cities,
      valueKey: "id",
      labelKey: "city_name",
    },
    {
      key: "nearestBranch",
      label: "Nearest Branch",
      placeholder: "Select nearest branch",
      type: "dropdown",
      options: branches,
      valueKey: "id",
      labelKey: "branch_name",
    },
  ];

  return (
    <Box
      className="scrollbar-hide"
      sx={{
        maxHeight: "70vh",
        overflowY: "auto",
        pr: 1, // prevent content cutoff
        "&::-webkit-scrollbar": {
          width: "6px",
        },
        "&::-webkit-scrollbar-track": {
          background: "#f1f1f1",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb": {
          background: "#bdbdbd",
          borderRadius: "10px",
        },
        "&::-webkit-scrollbar-thumb:hover": {
          background: "#9e9e9e",
        },
      }}
    >
      <Box sx={{ fontFamily: "Inter" }}>
        {/* 1. Guardian Name */}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: { xs: "16px", sm: "17px", md: "18px" },
            lineHeight: "100%",
            mb: 1,
            color: "#111111",
          }}
        >
          Guardian Name
          <span style={{ color: "red" }}> *</span>
        </Typography>

        <TextField
          fullWidth
          placeholder="Enter your guardian name"
          value={formData.guardianName}
          onChange={(e) => {
            const onlyLetters = e.target.value.replace(/[^A-Za-z ]/g, "");
            setFormData({ ...formData, guardianName: onlyLetters });
            setErrors({ ...errors, guardianName: "" });
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 42,
              borderRadius: "10px",
              mb: errors.guardianName ? 0 : 2,
            },
          }}
        />

        {errors.guardianName && (
          <FormHelperText sx={{ color: "red" }}>
            {errors.guardianName}
          </FormHelperText>
        )}

        {/* 2. Guardian Mobile Number */}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: { xs: "16px", sm: "17px", md: "18px" },
            mb: 1,
            color: "#111111",
          }}
        >
          Guardian Mobile Number
          <span style={{ color: "red" }}> *</span>
        </Typography>

        <TextField
          fullWidth
          placeholder="Enter Your Registered Mobile Number"
          value={formData.guardianMobile}
          onChange={(e) => {
            const onlyNums = e.target.value.replace(/\D/g, ""); // <-- only digits
            setFormData({ ...formData, guardianMobile: onlyNums });
            setErrors({ ...errors, guardianMobile: "" });
          }}
          error={!!errors.guardianMobile}
          helperText={errors.guardianMobile}
          InputProps={{
            startAdornment: (
              <Box sx={{ display: "flex", alignItems: "center", pr: 1 }}>
                <Select
                  value={formData.guardianCountryCode}
                  onChange={(e) =>
                    setFormData({
                      ...formData,
                      guardianCountryCode: e.target.value,
                    })
                  }
                  sx={{
                    "& .MuiOutlinedInput-notchedOutline": { border: "none" },
                    "& .MuiSelect-select": {
                      display: "flex",
                      alignItems: "center",
                      padding: 0,
                      pr: 2,
                      gap: "6px",
                    },
                    "& .MuiSelect-icon": { display: "none" },
                    minWidth: { xs: 62, sm: 75 },
                  }}
                >
                  {isdCodeList.map((opt) => (
                    <MenuItem key={opt.locale_code} value={opt.phone_code}>
                      <Box
                        sx={{
                          display: "flex",
                          alignItems: "center",
                          gap: 1,
                          mt: 1.5,
                        }}
                      >
                        <img
                          src={`https://flagcdn.com/w40/${opt.locale_code.toLowerCase()}.png`}
                          width="22"
                          height="16"
                          style={{ borderRadius: 2 }}
                        />
                        +{opt.phone_code}
                      </Box>
                    </MenuItem>
                  ))}
                </Select>

                <Box
                  sx={{
                    width: "1px",
                    height: "22px",
                    backgroundColor: "#D9D9D9",
                    mx: 1,
                  }}
                />
              </Box>
            ),
          }}
          sx={{
            "& .MuiOutlinedInput-root": {
              height: 42,
              borderRadius: "10px",
              mb: errors.guardianMobile ? 0 : 2,
            },
          }}
        />

        {/* 3. Select Dream Destination */}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: { xs: "16px", sm: "17px", md: "18px" },
            mb: 1,
            color: "#111111",
          }}
        >
          Select Dream Destination
          <span style={{ color: "red" }}> *</span>
        </Typography>
        <Select
          fullWidth
          displayEmpty
          value={formData.dreamDestination}
          onChange={(e) => {
            setFormData({ ...formData, dreamDestination: e.target.value });
            setErrors({ ...errors, dreamDestination: "" });
          }}
          sx={{
            height: 42,
            borderRadius: "10px",
            mb: 2,
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: 400,
          }}
        >
          {/* Placeholder */}
          <MenuItem
            value=""
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "0px",
              verticalAlign: "middle",
              opacity: 0.7,
            }}
          >
            <span style={{ fontStyle: "normal", color: "#333333" }}>
              Select Dream Destination
            </span>
          </MenuItem>

          {/* Dynamic List */}
          {dreamDestinations.map((opt) => (
            <MenuItem
              key={opt.id}
              value={opt.id}
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "100%",
              }}
            >
              {opt.country_name}
            </MenuItem>
          ))}
        </Select>
        {errors.dreamDestination && (
          <FormHelperText sx={{ color: "red", mt: -1 }}>
            {errors.dreamDestination}
          </FormHelperText>
        )}

        {/* 4. Nearest Area of Study */}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: { xs: "16px", sm: "17px", md: "18px" },
            mb: 1,
            color: "#111111",
          }}
        >
          Nearest Area of Study
          <span style={{ color: "red" }}> *</span>
        </Typography>
        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: 2,
          }}
        >
          {/* Broad Field */}
          <Box sx={{ flex: 1 }}>
            <Select
              fullWidth
              displayEmpty
              value={formData.broadField}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  broadField: e.target.value,
                  narrowField: "",
                });
                setErrors({ ...errors, broadField: "", narrowField: "" }); // clear both
              }}
              error={!!errors.broadField}
              sx={{
                height: 42,
                borderRadius: "10px",
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 400,
              }}
            >
              <MenuItem value="" sx={{ opacity: 0.7 }}>
                Select Broad Field
              </MenuItem>

              {broadFields.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.broad_field_name}
                </MenuItem>
              ))}
            </Select>

            {errors.broadField && (
              <FormHelperText sx={{ color: "red" }}>
                {errors.broadField}
              </FormHelperText>
            )}
          </Box>

          {/* Narrow Field */}
          <Box sx={{ flex: 1 }}>
            <Select
              fullWidth
              displayEmpty
              value={formData.narrowField}
              onChange={(e) => {
                setFormData({ ...formData, narrowField: e.target.value });
                setErrors({ ...errors, narrowField: "" });
              }}
              error={!!errors.narrowField}
              sx={{
                height: 42,
                borderRadius: "10px",
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 400,
              }}
            >
              <MenuItem value="" sx={{ opacity: 0.7 }}>
                Select Narrow Field
              </MenuItem>

              {!formData.broadField && (
                <MenuItem disabled sx={{ opacity: 0.6 }}>
                  Please select broad field first
                </MenuItem>
              )}

              {formData.broadField &&
                narrowFields.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.narrow_field_name}
                  </MenuItem>
                ))}
            </Select>

            {errors.narrowField && (
              <FormHelperText sx={{ color: "red" }}>
                {errors.narrowField}
              </FormHelperText>
            )}
          </Box>
        </Box>

        {/* 5. Select Location */}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: { xs: "16px", sm: "17px", md: "18px" },
            mb: 1,
            mt: 1,
            color: "#111111",
          }}
        >
          Select Location
          <span style={{ color: "red" }}> *</span>
        </Typography>

        <Box
          sx={{
            display: "flex",
            flexDirection: { xs: "column", md: "row" },
            gap: { xs: 2, md: 2 },
          }}
        >
          {/* Country */}
          <Box sx={{ width: "100%" }}>
            <Select
              fullWidth
              displayEmpty
              value={formData.country}
              onChange={(e) => {
                setFormData({
                  ...formData,
                  country: e.target.value,
                  state: "",
                  city: "",
                });
                setErrors({ ...errors, country: "", state: "", city: "" });
              }}
              sx={{
                height: 42,
                borderRadius: "10px",
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 400,
                mb: 0.5,
              }}
            >
              <MenuItem value="" sx={{ opacity: 0.7 }}>
                <span>Select Country</span>
              </MenuItem>

              {countries.map((opt) => (
                <MenuItem key={opt.id} value={opt.id}>
                  {opt.country_name}
                </MenuItem>
              ))}
            </Select>

            {errors.country && (
              <FormHelperText sx={{ color: "red", mt: -0.5 }}>
                {errors.country}
              </FormHelperText>
            )}
          </Box>

          {/* State */}
          <Box sx={{ width: "100%" }}>
            <Select
              fullWidth
              displayEmpty
              value={formData.state}
              onChange={(e) => {
                setFormData({ ...formData, state: e.target.value, city: "" });
                setErrors({ ...errors, state: "", city: "" });
              }}
              sx={{
                height: 42,
                borderRadius: "10px",
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 400,
                mb: 0.5,
              }}
            >
              <MenuItem value="" sx={{ opacity: 0.7 }}>
                <span>Select State</span>
              </MenuItem>

              {!formData.country && (
                <MenuItem disabled sx={{ opacity: 0.6 }}>
                  Please select country first
                </MenuItem>
              )}

              {formData.country &&
                states.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.state_name}
                  </MenuItem>
                ))}
            </Select>

            {errors.state && (
              <FormHelperText sx={{ color: "red", mt: -0.5 }}>
                {errors.state}
              </FormHelperText>
            )}
          </Box>

          {/* City */}
          <Box sx={{ width: "100%" }}>
            <Select
              fullWidth
              displayEmpty
              value={formData.city}
              onChange={(e) => {
                setFormData({ ...formData, city: e.target.value });
                setErrors({ ...errors, city: "" });
              }}
              sx={{
                height: 42,
                borderRadius: "10px",
                fontFamily: "Inter",
                fontSize: "16px",
                fontWeight: 400,
                mb: 0.5,
              }}
            >
              <MenuItem value="" sx={{ opacity: 0.7 }}>
                <span>Select City</span>
              </MenuItem>

              {!formData.state && (
                <MenuItem disabled sx={{ opacity: 0.5 }}>
                  Please select country and state first
                </MenuItem>
              )}

              {formData.state &&
                cities.map((opt) => (
                  <MenuItem key={opt.id} value={opt.id}>
                    {opt.city_name}
                  </MenuItem>
                ))}
            </Select>

            {errors.city && (
              <FormHelperText sx={{ color: "red", mt: -0.5 }}>
                {errors.city}
              </FormHelperText>
            )}
          </Box>
        </Box>

        {/* 6. Nearest SIEC Branch */}
        <Typography
          sx={{
            fontFamily: "Inter",
            fontWeight: 500,
            fontSize: { xs: "16px", sm: "17px", md: "18px" },
            mb: 1,
            mt: 1,
            color: "#111111",
          }}
        >
          Nearest SIEC Branch
          <span style={{ color: "red" }}> *</span>
        </Typography>

        <Select
          fullWidth
          displayEmpty
          value={formData.nearestBranch}
          onChange={(e) => {
            setFormData({ ...formData, nearestBranch: e.target.value });
            setErrors({ ...errors, nearestBranch: "" });
          }}
          sx={{
            height: 42,
            borderRadius: "10px",
            mb: 2,
            fontFamily: "Inter",
            fontSize: "16px",
            fontWeight: 400,
          }}
        >
          {/* Placeholder */}
          <MenuItem
            value=""
            sx={{
              fontFamily: "Inter",
              fontWeight: 400,
              fontStyle: "normal",
              fontSize: "16px",
              lineHeight: "100%",
              letterSpacing: "0px",
              verticalAlign: "middle",
              opacity: 0.7,
            }}
          >
            <span style={{ fontStyle: "normal" }}>Select Nearest Branch</span>
          </MenuItem>

          {/* Dynamic Options */}
          {branches.map((opt) => (
            <MenuItem
              key={opt.id}
              value={opt.id}
              sx={{
                fontFamily: "Inter",
                fontWeight: 400,
                fontSize: "16px",
                lineHeight: "100%",
              }}
            >
              {opt.branch_name}
            </MenuItem>
          ))}
        </Select>
        {errors.nearestBranch && (
          <FormHelperText sx={{ color: "red", mt: -1 }}>
            {errors.nearestBranch}
          </FormHelperText>
        )}
      </Box>
    </Box>
  );
});

export default StepperForm;
